/**
 * IntegrationGuide — Slide-in panel from the right providing contextual
 * integration guidance for each billing admin screen.
 *
 * Shows code examples, API endpoints, webhook payloads, and step-by-step
 * configuration instructions specific to the current page context.
 *
 * Now uses the unified SlidePanel component for beautiful animations.
 *
 * @module packages/billing-admin/src/components/IntegrationGuide
 * @since 2026-06-08
 */
import React, { useState, createContext, useContext } from 'react';
import SlidePanel from './SlidePanel';

// ---------------------------------------------------------------------------
// Guide Content Types
// ---------------------------------------------------------------------------

export interface GuideSection {
  title: string;
  content?: string;
  code?: string;
  language?: string;
}

export interface GuideContent {
  /** Page title */
  title: string;
  /** Brief intro */
  description: string;
  /** Structured sections */
  sections: GuideSection[];
}

// ---------------------------------------------------------------------------
// Context for controlling the panel
// ---------------------------------------------------------------------------

interface GuideContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const GuideContext = createContext<GuideContextValue>({
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
});

export function useGuide(): GuideContextValue {
  return useContext(GuideContext);
}

export function GuideProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <GuideContext.Provider value={{ isOpen, open: () => setIsOpen(true), close: () => setIsOpen(false), toggle: () => setIsOpen((v) => !v) }}>
      {children}
    </GuideContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Guide Toggle Button (goes in the header)
// ---------------------------------------------------------------------------

export function GuideToggleButton(): React.JSX.Element {
  const { toggle, isOpen } = useGuide();
  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
        isOpen
          ? 'border-cyan-300 bg-cyan-50 text-cyan-700'
          : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50'
      }`}
    >
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
      Integration Guide
    </button>
  );
}

// ---------------------------------------------------------------------------
// Slide-in Panel (uses SlidePanel for animation)
// ---------------------------------------------------------------------------

interface IntegrationGuidePanelProps {
  guide: GuideContent;
}

export function IntegrationGuidePanel({ guide }: IntegrationGuidePanelProps): React.JSX.Element {
  const { isOpen, close } = useGuide();
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  function handleCopy(code: string, idx: number): void {
    void navigator.clipboard.writeText(code);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  }

  return (
    <SlidePanel
      open={isOpen}
      onClose={close}
      size="md"
      title={guide.title}
      category="Integration Guide"
      footer={
        <p className="text-xs text-slate-400">
          Full API documentation at <span className="font-mono text-cyan-600">finance.rule42.ai/docs</span>
        </p>
      }
    >
      <div className="px-6 py-6">
        <p className="text-sm text-slate-600 leading-relaxed">{guide.description}</p>

        <div className="mt-6 space-y-6">
          {guide.sections.map((section, idx) => (
            <div key={idx} className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
              <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
              {section.content && (
                <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{section.content}</p>
              )}
              {section.code && (
                <div className="mt-3 relative">
                  <button
                    onClick={() => handleCopy(section.code!, idx)}
                    className="absolute right-2 top-2 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-medium text-slate-500 hover:bg-slate-50"
                  >
                    {copiedIdx === idx ? '✓ Copied' : 'Copy'}
                  </button>
                  <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-200 leading-relaxed">
                    <code>{section.code}</code>
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SlidePanel>
  );
}
